import type { HeroBlock as HeroBlockProps } from '@/payload-types'
import React from 'react'
import { Badge } from '@/components'
import Image from 'next/image'
import { isMedia, cn } from '@/lib/utils'
import { cva } from 'class-variance-authority'

type Props = {
  className?: string
} & HeroBlockProps

const heroBlockVariants = cva('relative border-b bg-accent', {
  variants: {
    hasImage: {
      true: 'text-background',
      false: 'text-foreground',
    },
  },
  defaultVariants: {
    hasImage: false,
  },
})

export function HeroBlock({ className, title, overline, lead, image }: Props): React.ReactNode {
  const heroImage = image && isMedia(image) ? image : false

  return (
    <header
      className={cn(
        heroBlockVariants({
          hasImage: !!heroImage,
        }),
        className,
      )}
    >
      <div className="relative z-20 max-w-7xl mx-auto flex flex-col gap-2 items-center justify-end min-h-56 p-4 pb-10">
        {overline ? <Badge>{overline}</Badge> : null}
        <h2 className="text-xl lg:text-3xl text-center text-shadow-2xs font-semibold leading-tight">
          {title}
        </h2>
        <p className="max-w-screen-sm text-sm text-shadow-2xs leading-tight text-center opacity-75">
          {lead}
        </p>
      </div>
      {heroImage && heroImage.url ? (
        <>
          <figure className="absolute inset-0 z-0">
            <Image src={heroImage.url} alt={heroImage.alt || title} className="object-cover" fill />
          </figure>
          <div className="absolute z-10 inset-0 bg-linear-to-b from-transparent to-black/80" />
        </>
      ) : null}
    </header>
  )
}
